import {KeyboardEvent, MouseEvent, useCallback, useContext, useState, type ReactNode} from 'react';

import {useTranslation} from 'react-i18next';

import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import InterestsOutlinedIcon from '@mui/icons-material/InterestsOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import {Box, Chip, Collapse, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';

import {kanbanMatchCardPrimaryButtonSizeSx} from '../BoardViewStyles';

import {AcceptedMatchActions} from './AcceptedMatchActions';
import {MatchAcceptanceActions} from './MatchAcceptanceActions';
import {EmpionButton} from '../../../../../../../components/EmpionButton/EmpionButton';
import {EmpionMatchScoresGrid} from '../../../../../../../components/EmpionMatchScoresGrid/EmpionMatchScoresGrid';
import {CompanyContext} from '../../../../../../../contexts/CompanyContext/CompanyContext';
import {LanguageContext} from '../../../../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiWrite} from '../../../../../../../hooks/useApiWrite';
import {useDownloadPdfProfile} from '../../../../../../../hooks/useDownloadPdfProfile';
import {accent, greenColors, orangeColors} from '../../../../../../../layouts/theme/designSystem';
import {Patch} from '../../../../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../../../../lib/api/PATCH/PatchRoutes';
import {CompanyRejectionReasonBody} from '../../../../../../../lib/api/RequestBodies/shared-recruiting/SharedRecruitingRequestBodies';
import {MatchStatus} from '../../../../../../../types/MatchStatus/MatchStatus';
import {
  isKeyFactLocationInsight,
  KeyFactInsight,
  KeyFactLocationInsight,
  MatchData,
} from '../../../../../../../types/shared-recruiting-types';
import {LanguageProfile} from '../../../../../../../types/shared-types';
import {SupportedLanguages} from '../../../../../../../types/SupportedLanguages';
import {safelyUseLanguageProfile} from '../../../../../../../utils/safelyUseLanguageProfile';
import {sanitizeContent} from '../../../../../../../utils/sanitizeHtml/sanitizeHtml';
import {TeamValue} from '../../../../../culture/dashboard/types/CultureInformationTypes';
import {RejectMatch} from '../../NewMatchDetails/RejectMatch';

type Props = {
  match: MatchData;
  onMoveToContacted?: (matchId: string) => Promise<void> | void;
  onMoveToRejected?: (matchId: string) => Promise<void> | void;
  isStatusUpdating?: boolean;
};

const formatScore = (score?: number | null): string => {
  if (score === null || score === undefined) return '--%';
  return `${score}%`;
};

const cultureTeamValueHtmlForLang = (teamValue: TeamValue, lang: SupportedLanguages): string => {
  const primary = lang === 'de' ? teamValue.htmlDe : teamValue.htmlEn;
  const fallback = lang === 'de' ? teamValue.htmlEn : teamValue.htmlDe;
  const raw = (primary?.trim() ? primary : fallback) || '';
  return sanitizeContent(raw);
};

/** Filled chip tints by pipeline status (aligned with contacted = orange, interview = green columns). */
const matchStatusChipToneSx = (status: string | undefined) => {
  const id = status?.trim().toLowerCase() ?? '';
  if (id === MatchStatus.Contacted || id === 'contacted') {
    return {
      bgcolor: alpha(orangeColors[100], 0.22),
      color: orangeColors[900],
    };
  }
  if (id === MatchStatus.Interview || id === 'interview') {
    return {
      bgcolor: greenColors[50],
      color: greenColors[700],
    };
  }
  if (id === MatchStatus.Hired || id === 'hired') {
    return {
      bgcolor: greenColors[4],
      color: greenColors[700],
    };
  }
  if (id === MatchStatus.Rejected || id === 'rejected') {
    return {
      bgcolor: alpha(orangeColors[100], 0.14),
      color: orangeColors[900],
    };
  }
  if (id === MatchStatus.OnHold || id === 'on_hold') {
    return {
      bgcolor: orangeColors[5],
      color: greenColors[700],
    };
  }
  return {
    bgcolor: greenColors[50],
    color: greenColors[700],
  };
};

const contactRowIconSx = {
  fontSize: 14,
  color: greenColors[600],
  flexShrink: 0,
  mt: '2px',
} as const;

const contactRowSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 0.5,
  minWidth: 0,
} as const;

/** Label + job title vs scores; `justify-content: space-between` on wide, stack in narrow cards. */
const jobPostingHeaderRowSx = {
  display: 'flex',
  flexDirection: 'row' as const,
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 1.25,
  mb: 1,
  '@container (max-width: 420px)': {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 1,
  },
} as const;

const jobPostingTitleBlockSx = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'flex-start',
  '@container (max-width: 420px)': {
    flex: 'none',
    width: '100%',
  },
} as const;

const jobPostingScoresBlockSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'flex-end',
  flexShrink: 0,
  '@container (max-width: 420px)': {
    alignItems: 'stretch',
    alignSelf: 'stretch',
    width: '100%',
  },
} as const;

const collapsibleShellSx = {
  borderRadius: '8px',
  overflow: 'hidden',
};

const sectionHeaderRowBaseSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 0.5,
  minHeight: 32,
  py: 0.5,
  px: 1,
  cursor: 'pointer',
  userSelect: 'none',
} as const;

type KeyFactVisualVariant = 'met' | 'notMet';

const keyFactRequirementNotMetCategories = new Set(['requirement_not_met', 'not_met', 'requirement_failed']);

const insightVisualVariant = (insight: {category?: string} | undefined): KeyFactVisualVariant => {
  const cat = insight?.category?.trim().toLowerCase() ?? '';
  if (keyFactRequirementNotMetCategories.has(cat)) return 'notMet';
  if (cat.endsWith('_not_met') && cat !== 'requirement_met') return 'notMet';
  return 'met';
};

const cultureKeyFactVisualVariant = (fitLevel: string | undefined): KeyFactVisualVariant => {
  const f = fitLevel?.trim().toLowerCase() ?? '';
  if (f === 'low_fit' || f === 'no_fit' || f === 'poor_fit') return 'notMet';
  return 'met';
};

const sectionHeaderRowSxFor = (variant: KeyFactVisualVariant) => ({
  ...sectionHeaderRowBaseSx,
  bgcolor: variant === 'notMet' ? orangeColors[5] : greenColors[50],
});

const sectionHeaderIconSxFor = (variant: KeyFactVisualVariant) =>
  variant === 'notMet' ? ({fontSize: 16, color: orangeColors[500], flexShrink: 0} as const) : sectionHeaderIconSx;

const keyFactTitleColor = (variant: KeyFactVisualVariant) =>
  variant === 'notMet' ? orangeColors[900] : greenColors[700];

const keyFactExpandChevronColor = (variant: KeyFactVisualVariant) =>
  variant === 'notMet' ? orangeColors[70] : greenColors[100];

const detailsBodyTextSxFor = (variant: KeyFactVisualVariant) => ({
  fontSize: '13px',
  lineHeight: 1.3,
  color: variant === 'notMet' ? orangeColors[900] : greenColors[900],
  '& p': {margin: 0, marginBottom: 0.25},
});

const expandedSectionBodySx = {
  px: 1,
  py: 1,
  bgcolor: 'white',
  border: `1px solid ${orangeColors[3]}`,
  borderRadius: '0 0 8px 8px',
};

type ApplicantStatusMenuKey = 'new' | 'accepted' | 'contacted' | 'onHold' | 'interview' | 'hired' | 'rejected';

const formatKeyFactScore = (score: number): string => {
  if (Number.isNaN(score)) {
    return '—';
  }
  if (score >= 0 && score <= 1) {
    return `${Math.round(score * 100)}%`;
  }
  return `${Math.round(score)}%`;
};

const sectionHeaderIconSx = {fontSize: 16, color: accent.icon, flexShrink: 0} as const;

const matchStatusToMenuKey = (status: string | undefined): ApplicantStatusMenuKey | null => {
  const id = status?.trim().toLowerCase() ?? '';
  if (id === 'on_hold') return 'onHold';
  if (id === 'accepted') return 'accepted';
  if (id === 'contacted') return 'contacted';
  if (id === 'interview') return 'interview';
  if (id === 'hired') return 'hired';
  if (id === 'rejected') return 'rejected';
  if (id === 'new' || id === 'pending') return 'new';
  return null;
};

export const KanbanMatchCardContent = ({
  match,
  onMoveToContacted,
  onMoveToRejected,
  isStatusUpdating = false,
}: Props) => {
  const [keyFactOpen, setKeyFactOpen] = useState({
    location: false,
    language: false,
    salary: false,
    culture: false,
    skills: false,
  });
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const lang = useContext(LanguageContext);
  const {t} = useTranslation('translation');
  const {t: tCard} = useTranslation('translation', {keyPrefix: 'companyApplicants.applicantsCard.cardDetails'});
  const {t: tTable} = useTranslation('translation', {keyPrefix: 'matchesPage.tableColumns'});
  const {t: tKanban} = useTranslation('translation', {keyPrefix: 'matchesPage.kanbanCard'});
  const {t: tStatusMenu} = useTranslation('translation', {keyPrefix: 'companyApplicants.applicantsCard.menuItems'});
  const {company} = useContext(CompanyContext);
  const {dispatchMessage} = useContext(SnackBarContext);
  const {downloadPdf, isLoading: isPdfDownloading} = useDownloadPdfProfile({
    companyId: company?.data?.resourceId || '',
    matchId: match.resourceId,
    // eslint-disable-next-line max-len
    fileName: `${match.applicant?.personalInformation.firstName}_${match.applicant?.personalInformation.lastName}_profile.pdf`,
    onError: () => dispatchMessage(tCard('noPdfData'), 'error'),
  });
  const companyId = company.data?.resourceId || '';

  const firstName = match.applicant?.personalInformation?.firstName ?? '';
  const lastName = match.applicant?.personalInformation?.lastName ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  const applicantEmail = (match.applicant?.personalInformation?.email ?? '').trim();
  const applicantPhone = (match.applicant?.personalInformation?.mobilePhoneNumber ?? '').trim();
  const position = match.companyJobPosting?.title || '—';
  const totalScore = formatScore(match.matchInformationOverview?.overallScore);
  const downloadProfilePdfLabel = tKanban('downloadProfilePdf');

  const handleDownloadProfile = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      await downloadPdf();
    },
    [downloadPdf]
  );

  const handleApprove = useCallback(async () => {
    if (!onMoveToContacted) return;
    await onMoveToContacted(match.resourceId);
  }, [match.resourceId, onMoveToContacted]);

  const handleRejectSuccess = useCallback(async () => {
    dispatchMessage(t('shared.tags.statusUpdate'), 'success');
    setRejectModalOpen(false);
    if (onMoveToRejected) {
      await onMoveToRejected(match.resourceId);
    }
  }, [dispatchMessage, match.resourceId, onMoveToRejected, setRejectModalOpen, t]);

  const {
    makeRequest: rejectMatch,
    isLoading: isRejecting,
    error: rejectError,
  } = useApiWrite<Patch.RejectMatchByCompany>({
    method: 'patch',
    path: PatchRoutes[Patch.RejectMatchByCompany](companyId, match.resourceId),
    onSuccess: handleRejectSuccess,
  });

  const handleOpenRejectModal = useCallback(() => {
    setRejectModalOpen(true);
  }, [setRejectModalOpen]);

  const handleReject = useCallback(
    (body: CompanyRejectionReasonBody) => {
      rejectMatch(body);
    },
    [rejectMatch]
  );
  const isFirstColumnCard = Boolean(onMoveToContacted);

  const statusMenuKey = matchStatusToMenuKey(match.status);
  const statusChipLabel = statusMenuKey ? String(tStatusMenu(statusMenuKey)) : match.status || '—';
  const showMatchStatusChip = match.status?.trim().toLowerCase() !== MatchStatus.Accepted;

  type KeyFactSectionId = 'location' | 'language' | 'salary' | 'culture' | 'skills';

  const toggleKeyFactSection = (id: KeyFactSectionId) => (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setKeyFactOpen((prev) => ({...prev, [id]: !prev[id]}));
  };

  const keyFactSectionKeyDown = (id: KeyFactSectionId) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      setKeyFactOpen((prev) => ({...prev, [id]: !prev[id]}));
    }
  };

  const renderInsightExplanationOnly = (insight: KeyFactInsight, variant: KeyFactVisualVariant) => (
    <Typography sx={detailsBodyTextSxFor(variant)}>{insight.explanation}</Typography>
  );

  const renderLocationBody = (insight: KeyFactLocationInsight, variant: KeyFactVisualVariant) => (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
      {renderInsightExplanationOnly(insight, variant)}
      {insight.inferredDistanceKm != null || insight.distanceSource ? (
        <Typography
          sx={{
            fontSize: '10px',
            lineHeight: 1.35,
            color: variant === 'notMet' ? orangeColors[70] : greenColors[600],
          }}
        >
          {insight.inferredDistanceKm != null
            ? String(tKanban('keyFacts.inferredDistanceKm', {value: insight.inferredDistanceKm}))
            : null}
          {insight.inferredDistanceKm != null && insight.distanceSource ? ' · ' : null}
          {insight.distanceSource
            ? String(
              tKanban(`keyFacts.distanceSource.${insight.distanceSource}`, {
                defaultValue: insight.distanceSource.replace(/_/g, ' '),
              })
            )
            : null}
        </Typography>
      ) : null}
    </Box>
  );

  const applicantKeyFacts = match.applicant?.keyFacts;
  const cultureInsightName = applicantKeyFacts?.culture?.cultureTypeName
    ? safelyUseLanguageProfile(lang, applicantKeyFacts.culture.cultureTypeName as LanguageProfile)
    : undefined;

  const keyFactSections: Array<{
    id: KeyFactSectionId;
    title: string;
    variant: KeyFactVisualVariant;
    /** Formatted match % for the section header (right of title, before chevron). */
    headerScore?: string;
    icon: ReactNode;
    body: ReactNode;
  }> = [];

  const locationKeyFact = applicantKeyFacts?.location;
  if (typeof locationKeyFact === 'string' && locationKeyFact.trim()) {
    keyFactSections.push({
      id: 'location',
      title: tKanban('keyFacts.location'),
      variant: 'met',
      icon: <LocationOnOutlinedIcon sx={sectionHeaderIconSxFor('met')} aria-hidden />,
      body: <Typography sx={detailsBodyTextSxFor('met')}>{locationKeyFact.trim()}</Typography>,
    });
  } else if (isKeyFactLocationInsight(locationKeyFact) && locationKeyFact.explanation) {
    const locationVariant = insightVisualVariant(locationKeyFact);
    keyFactSections.push({
      id: 'location',
      title: tKanban('keyFacts.location'),
      variant: locationVariant,
      headerScore: formatKeyFactScore(locationKeyFact.score),
      icon: <LocationOnOutlinedIcon sx={sectionHeaderIconSxFor(locationVariant)} aria-hidden />,
      body: renderLocationBody(locationKeyFact, locationVariant),
    });
  }
  if (applicantKeyFacts?.language?.explanation) {
    const languageVariant = insightVisualVariant(applicantKeyFacts.language);
    keyFactSections.push({
      id: 'language',
      title: tKanban('keyFacts.language'),
      variant: languageVariant,
      headerScore: formatKeyFactScore(applicantKeyFacts.language.score),
      icon: <LanguageOutlinedIcon sx={sectionHeaderIconSxFor(languageVariant)} aria-hidden />,
      body: renderInsightExplanationOnly(applicantKeyFacts.language, languageVariant),
    });
  }
  if (applicantKeyFacts?.salary && 'explanation' in applicantKeyFacts.salary) {
    const salaryVariant = insightVisualVariant(applicantKeyFacts.salary);
    keyFactSections.push({
      id: 'salary',
      title: tKanban('keyFacts.salary'),
      variant: salaryVariant,
      headerScore: formatKeyFactScore(applicantKeyFacts.salary.score),
      icon: <PaidOutlinedIcon sx={sectionHeaderIconSxFor(salaryVariant)} aria-hidden />,
      body: renderInsightExplanationOnly(applicantKeyFacts.salary, salaryVariant),
    });
  }
  const cultureInsight = applicantKeyFacts?.culture;
  const cultureTeamHtml = cultureInsight?.teamValue ? cultureTeamValueHtmlForLang(cultureInsight.teamValue, lang) : '';
  const hasCultureTeamHtml = Boolean(cultureTeamHtml.trim());

  if (
    cultureInsight &&
    (cultureInsightName ||
      cultureInsight.fitScorePercentage != null ||
      Boolean(cultureInsight.fitLevel?.trim()) ||
      hasCultureTeamHtml)
  ) {
    const cultureKeyFactVariant = cultureKeyFactVisualVariant(cultureInsight.fitLevel);
    const cultureHeaderScore =
      cultureInsight.fitScorePercentage != null ? `${Math.round(cultureInsight.fitScorePercentage)}%` : undefined;
    const hasCultureBody = Boolean(cultureInsightName) || hasCultureTeamHtml;
    keyFactSections.push({
      id: 'culture',
      title: tKanban('keyFacts.culture'),
      variant: cultureKeyFactVariant,
      headerScore: cultureHeaderScore,
      icon: <InterestsOutlinedIcon sx={sectionHeaderIconSxFor(cultureKeyFactVariant)} aria-hidden />,
      body: hasCultureBody ? (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
          {cultureInsightName ? (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                color: keyFactTitleColor(cultureKeyFactVariant),
                lineHeight: 1.3,
              }}
            >
              {cultureInsightName}
            </Typography>
          ) : null}
          {hasCultureTeamHtml ? (
            <Typography
              component='div'
              sx={{
                ...detailsBodyTextSxFor(cultureKeyFactVariant),
                fontSize: '13px',
                '& p': {margin: '8px 0'},
                '& p:first-of-type': {marginTop: 0},
                '& p:last-of-type': {marginBottom: 0},
              }}
              dangerouslySetInnerHTML={{__html: cultureTeamHtml}}
            />
          ) : null}
        </Box>
      ) : null,
    });
  }
  if (applicantKeyFacts?.relevantSkills?.length) {
    keyFactSections.push({
      id: 'skills',
      title: tKanban('keyFacts.relevantSkills'),
      variant: 'met',
      icon: <HandymanOutlinedIcon sx={sectionHeaderIconSxFor('met')} aria-hidden />,
      body: (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
          {applicantKeyFacts.relevantSkills.map((skill, index) => (
            <Box key={`${skill.name}-${index}`} sx={{display: 'flex', flexDirection: 'column', gap: 0.35}}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: greenColors[700],
                  lineHeight: 1.3,
                }}
              >
                {skill.name}
              </Typography>
              {skill.evidence ? <Typography sx={detailsBodyTextSxFor('met')}>{skill.evidence}</Typography> : null}
            </Box>
          ))}
        </Box>
      ),
    });
  }

  const scoreBreakdownItems = [
    {
      key: 'total' as const,
      label: tKanban('scoreTotal'),
      value: totalScore,
    },
    {
      key: 'job' as const,
      label: tTable('job'),
      value: formatScore(match.matchInformationOverview?.jobMatchScore),
    },
    {
      key: 'test' as const,
      label: tTable('test'),
      value: formatScore(match.matchInformationOverview?.assessmentScore),
    },
    {
      key: 'culture' as const,
      label: tTable('culture'),
      value: formatScore(match.matchInformationOverview?.culturalMatchScore),
    },
  ];

  return (
    <Box sx={{containerType: 'inline-size', minWidth: 0}}>
      <Box sx={{mb: 1.25}}>
        <Box sx={jobPostingHeaderRowSx}>
          <Box sx={jobPostingTitleBlockSx}>
            <Typography sx={{fontSize: '12px', lineHeight: 1.3, color: greenColors[70], mb: 0.5}}>
              {tTable('position')}
            </Typography>
            <Typography
              sx={{
                fontSize: '15px',
                lineHeight: 1.4,
                fontWeight: 600,
                color: greenColors[700],
                minWidth: 0,
              }}
            >
              {position}
            </Typography>
          </Box>
          <Box onClick={(e) => e.stopPropagation()} sx={jobPostingScoresBlockSx}>
            <Box data-tour='matches.card.scores'>
              <EmpionMatchScoresGrid
                ariaLabel={`${tKanban('scoreTotal')}, ${tTable('job')}, ${tTable('test')}, ${tTable('culture')}`}
                items={scoreBreakdownItems}
                variant='small'
              />
            </Box>
          </Box>
        </Box>
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          <Typography variant='h4' sx={{flex: '1 1 auto', minWidth: 0, mb: 0}}>
            {fullName || '—'}
          </Typography>
          {showMatchStatusChip ? (
            <Chip
              size='small'
              variant='filled'
              label={statusChipLabel}
              aria-label={statusChipLabel}
              sx={{
                height: 22,
                maxWidth: {xs: '100%', sm: 'min(200px, 48%)'},
                flexShrink: 0,
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                ...matchStatusChipToneSx(match.status),
                '& .MuiChip-label': {
                  px: 0.75,
                  py: 0,
                  fontSize: '11px',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          ) : null}
        </Box>
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            mt: 0.5,
            mb: 0.25,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              minWidth: 0,
              flex: '1 1 auto',
            }}
          >
            {applicantEmail ? (
              <Box sx={contactRowSx}>
                <EmailOutlinedIcon aria-hidden sx={contactRowIconSx} />
                <Typography
                  sx={{
                    fontSize: '12px',
                    lineHeight: 1.35,
                    color: greenColors[600],
                    wordBreak: 'break-word',
                  }}
                >
                  {applicantEmail}
                </Typography>
              </Box>
            ) : null}
            {applicantPhone ? (
              <Box sx={contactRowSx}>
                <PhoneOutlinedIcon aria-hidden sx={contactRowIconSx} />
                <Typography
                  sx={{
                    fontSize: '12px',
                    lineHeight: 1.35,
                    color: greenColors[600],
                    wordBreak: 'break-word',
                  }}
                >
                  {applicantPhone}
                </Typography>
              </Box>
            ) : null}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 0.5,
              flexShrink: 0,
              maxWidth: '100%',
              width: {xs: '100%', sm: 'auto'},
              ml: {xs: 0, sm: 'auto'},
            }}
          >
            <EmpionButton
              data-tour='matches.card.downloadProfile'
              size='small'
              variant='primary'
              startIcon={<PictureAsPdfOutlinedIcon aria-hidden />}
              onClick={handleDownloadProfile}
              disabled={isPdfDownloading}
              aria-label={downloadProfilePdfLabel}
              sx={kanbanMatchCardPrimaryButtonSizeSx}
            >
              {downloadProfilePdfLabel}
            </EmpionButton>
          </Box>
        </Box>
      </Box>

      <Box
        data-tour='matches.card.expandableSections'
        onClick={(e) => e.stopPropagation()}
        sx={{mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5}}
      >
        {keyFactSections.map((section) => {
          const onKeyFactHeaderClick = toggleKeyFactSection(section.id);
          const onKeyFactHeaderKeyDown = keyFactSectionKeyDown(section.id);
          const {variant, headerScore} = section;
          return (
            <Box key={section.id} sx={collapsibleShellSx}>
              <Box
                onClick={onKeyFactHeaderClick}
                onKeyDown={onKeyFactHeaderKeyDown}
                role='button'
                tabIndex={0}
                aria-expanded={keyFactOpen[section.id]}
                sx={sectionHeaderRowSxFor(variant)}
              >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0}}>
                  {section.icon}
                  <Typography
                    sx={{
                      fontSize: '12px',
                      lineHeight: 1.2,
                      fontWeight: 600,
                      color: keyFactTitleColor(variant),
                      minWidth: 0,
                    }}
                  >
                    {section.title}
                  </Typography>
                </Box>
                {headerScore ? (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      lineHeight: 1.2,
                      fontWeight: 700,
                      color: keyFactTitleColor(variant),
                      flexShrink: 0,
                      mr: 0.5,
                    }}
                  >
                    {headerScore}
                  </Typography>
                ) : null}
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20,
                    color: keyFactExpandChevronColor(variant),
                    flexShrink: 0,
                    transform: keyFactOpen[section.id] ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </Box>
              <Collapse in={keyFactOpen[section.id]} timeout='auto' unmountOnExit>
                <Box sx={expandedSectionBodySx}>{section.body}</Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
      {isFirstColumnCard ? (
        <>
          <MatchAcceptanceActions
            onApprove={handleApprove}
            onReject={handleOpenRejectModal}
            approveDisabled={isStatusUpdating}
            rejectDisabled={isStatusUpdating || isRejecting || match.status === 'rejected'}
          />
          <RejectMatch
            isModalOpen={rejectModalOpen}
            setModalOpen={setRejectModalOpen}
            onReject={handleReject}
            isLoading={isRejecting}
            error={rejectError}
          />
        </>
      ) : (
        <AcceptedMatchActions matchId={match.resourceId} />
      )}
    </Box>
  );
};
